import { useEffect, useMemo, useState } from "react";
import api from "../lib/api";
import PageHeader from "../components/PageHeader";
import { Card } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Plus, Pencil, Trash2, Percent, Search } from "lucide-react";
import { toast } from "sonner";

const EMPTY = {
  name: "", discount_type: "percent", discount_value: "",
  item_ids: [], group_names: [], starts_at: "", ends_at: "", active: true,
};

function eur(v) { return `${(Number(v) || 0).toFixed(2)} €`; }

// ISO <-> datetime-local
function toLocal(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "";
  const l = new Date(d.getTime() - d.getTimezoneOffset() * 60000);
  return l.toISOString().slice(0, 16);
}
function toISO(local) {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}
function fmtRange(s, e) {
  const f = (x) => (x ? new Date(x).toLocaleString("de-DE", { dateStyle: "short", timeStyle: "short" }) : "—");
  return `${f(s)} → ${f(e)}`;
}

export default function Offers() {
  const [offers, setOffers] = useState([]);
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [itemQuery, setItemQuery] = useState("");
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [o, it, g] = await Promise.all([
        api.get("/offers"),
        api.get("/items"),
        api.get("/product-groups"),
      ]);
      setOffers(o.data || []);
      setItems((it.data || []).filter((x) => x.type === "finished"));
      setGroups(g.data || []);
    } catch (e) { toast.error("Laden fehlgeschlagen"); }
    setLoading(false);
  };
  useEffect(() => { load(); }, []);

  const itemsById = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const filteredItems = useMemo(() => {
    const q = itemQuery.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => `${i.name} ${i.sku}`.toLowerCase().includes(q));
  }, [items, itemQuery]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setItemQuery(""); setOpen(true); };
  const openEdit = (o) => {
    setEditId(o.id);
    setForm({
      name: o.name || "", discount_type: o.discount_type || "percent",
      discount_value: String(o.discount_value ?? ""),
      item_ids: o.item_ids || [], group_names: o.group_names || [],
      starts_at: toLocal(o.starts_at), ends_at: toLocal(o.ends_at),
      active: !!o.active,
    });
    setItemQuery("");
    setOpen(true);
  };

  const toggleItem = (id) => setForm((f) => ({
    ...f, item_ids: f.item_ids.includes(id) ? f.item_ids.filter((x) => x !== id) : [...f.item_ids, id],
  }));
  const toggleGroup = (name) => setForm((f) => ({
    ...f, group_names: f.group_names.includes(name) ? f.group_names.filter((x) => x !== name) : [...f.group_names, name],
  }));

  const save = async () => {
    if (!form.name.trim()) { toast.error("Name fehlt"); return; }
    if (!(Number(form.discount_value) > 0)) { toast.error("Rabattwert muss größer 0 sein"); return; }
    if (form.discount_type === "percent" && Number(form.discount_value) >= 100) { toast.error("Prozentwert muss unter 100 liegen"); return; }
    if (form.item_ids.length === 0 && form.group_names.length === 0) { toast.error("Mindestens ein Produkt oder eine Warengruppe wählen"); return; }
    const payload = {
      name: form.name.trim(),
      discount_type: form.discount_type,
      discount_value: Number(form.discount_value),
      item_ids: form.item_ids,
      group_names: form.group_names,
      starts_at: toISO(form.starts_at),
      ends_at: toISO(form.ends_at),
      active: form.active,
    };
    setSaving(true);
    try {
      if (editId) await api.put(`/offers/${editId}`, payload);
      else await api.post("/offers", payload);
      toast.success("Aktion gespeichert");
      setOpen(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen"); }
    setSaving(false);
  };

  const toggleActive = async (o) => {
    try { await api.patch(`/offers/${o.id}/toggle`); load(); }
    catch { toast.error("Umschalten fehlgeschlagen"); }
  };
  const remove = async (o) => {
    if (!window.confirm(`Aktion „${o.name}" löschen?`)) return;
    try { await api.delete(`/offers/${o.id}`); toast.success("Gelöscht"); load(); }
    catch { toast.error("Löschen fehlgeschlagen"); }
  };

  const scopeLabel = (o) => {
    const parts = [];
    if ((o.item_ids || []).length) parts.push(`${o.item_ids.length} Produkt(e)`);
    if ((o.group_names || []).length) parts.push(`${o.group_names.length} Gruppe(n)`);
    return parts.join(" + ") || "—";
  };
  const discountLabel = (o) => o.discount_type === "percent" ? `−${o.discount_value}%` : `−${eur(o.discount_value)} / Einheit`;

  return (
    <div className="space-y-6" data-testid="page-offers">
      <PageHeader eyebrow="Vertrieb" title="Sonderangebote"
                  description="Zeitlich begrenzte B2B-Rabatte für Produkte oder ganze Warengruppen. Wirken automatisch im Kundenportal und im Bestellformular." />

      <div className="flex justify-end">
        <Button data-testid="btn-new-offer" onClick={openNew}
                className="rounded-full bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
          <Plus size={16} className="mr-1" /> Neue Aktion
        </Button>
      </div>

      <Card className="p-0 overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Rabatt</TableHead>
              <TableHead>Umfang</TableHead>
              <TableHead>Zeitraum</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Aktion</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {offers.map((o) => (
              <TableRow key={o.id} data-testid={`offer-row-${o.id}`}>
                <TableCell className="font-semibold">{o.name}</TableCell>
                <TableCell>
                  <Badge className="bg-[var(--pop-pink)] text-white inline-flex items-center gap-1">
                    <Percent size={11} /> {discountLabel(o)}
                  </Badge>
                </TableCell>
                <TableCell className="text-sm text-[var(--text-muted)]">{scopeLabel(o)}</TableCell>
                <TableCell className="text-sm text-[var(--text-muted)] whitespace-nowrap">{fmtRange(o.starts_at, o.ends_at)}</TableCell>
                <TableCell>
                  <div className="flex items-center gap-2">
                    <Switch checked={o.active} onCheckedChange={() => toggleActive(o)} data-testid={`offer-toggle-${o.id}`} />
                    {o.is_live
                      ? <Badge className="bg-green-600 text-white" data-testid={`offer-live-${o.id}`}>Läuft</Badge>
                      : <Badge variant="outline">{o.active ? "Geplant/abgelaufen" : "Inaktiv"}</Badge>}
                  </div>
                </TableCell>
                <TableCell className="text-right whitespace-nowrap">
                  <Button variant="ghost" size="sm" onClick={() => openEdit(o)} data-testid={`offer-edit-${o.id}`}><Pencil size={15} /></Button>
                  <Button variant="ghost" size="sm" onClick={() => remove(o)} data-testid={`offer-delete-${o.id}`} className="text-red-600"><Trash2 size={15} /></Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && offers.length === 0 && (
              <TableRow><TableCell colSpan={6} className="text-center text-[var(--text-muted)] py-10" data-testid="offers-empty">Noch keine Aktionen angelegt.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </Card>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto" data-testid="dialog-offer">
          <DialogHeader><DialogTitle className="font-display text-2xl">{editId ? "Aktion bearbeiten" : "Neue Aktion"}</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name *</Label>
              <Input data-testid="offer-name" value={form.name} placeholder="z. B. Sommer-Aktion Salz"
                     onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Rabatt-Art</Label>
                <Select value={form.discount_type} onValueChange={(v) => setForm({ ...form, discount_type: v })}>
                  <SelectTrigger data-testid="offer-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="percent">Prozentual (%)</SelectItem>
                    <SelectItem value="amount">Fester Betrag (€ / Einheit)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>{form.discount_type === "percent" ? "Prozent" : "Betrag je Einheit (€)"}</Label>
                <Input data-testid="offer-value" type="number" step="0.01" value={form.discount_value}
                       onChange={(e) => setForm({ ...form, discount_value: e.target.value })} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Start</Label>
                <Input data-testid="offer-start" type="datetime-local" value={form.starts_at}
                       onChange={(e) => setForm({ ...form, starts_at: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Ende</Label>
                <Input data-testid="offer-end" type="datetime-local" value={form.ends_at}
                       onChange={(e) => setForm({ ...form, ends_at: e.target.value })} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Warengruppen</Label>
              <div className="flex flex-wrap gap-2">
                {groups.map((g) => (
                  <button key={g.id} type="button" onClick={() => toggleGroup(g.name)}
                          data-testid={`offer-group-${g.name}`}
                          className={`px-3 py-1 rounded-full text-sm border transition-colors ${form.group_names.includes(g.name) ? "bg-[var(--pop-pink)] text-white border-transparent" : "bg-white text-neutral-600 border-neutral-300"}`}>
                    {g.name}
                  </button>
                ))}
                {groups.length === 0 && <span className="text-sm text-[var(--text-muted)]">Keine Warengruppen</span>}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Einzelne Produkte ({form.item_ids.length} gewählt)</Label>
              <div className="relative">
                <Search size={14} className="absolute left-2.5 top-2.5 text-neutral-400" />
                <Input data-testid="offer-item-search" className="pl-8" placeholder="Produkt suchen…" value={itemQuery}
                       onChange={(e) => setItemQuery(e.target.value)} />
              </div>
              <div className="max-h-48 overflow-y-auto rounded-xl border border-neutral-200 divide-y">
                {filteredItems.map((i) => (
                  <label key={i.id} className="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-neutral-50" data-testid={`offer-item-${i.sku}`}>
                    <input type="checkbox" checked={form.item_ids.includes(i.id)} onChange={() => toggleItem(i.id)}
                           className="w-4 h-4 accent-[var(--pop-pink)]" />
                    <span className="font-medium">{i.name}</span>
                    <span className="text-neutral-400 font-mono text-xs">{i.sku}</span>
                  </label>
                ))}
                {filteredItems.length === 0 && <div className="px-3 py-2 text-sm text-[var(--text-muted)]">Keine Treffer</div>}
              </div>
            </div>

            <div className="flex items-center gap-3 pt-1">
              <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="offer-active" />
              <span className="text-sm">Aktiv (wird im Zeitraum automatisch angewendet)</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Abbrechen</Button>
            <Button data-testid="offer-save" onClick={save} disabled={saving}
                    className="bg-[var(--pop-yellow)] text-black hover:bg-[var(--pop-yellow-dark)] font-bold">
              {saving ? "Speichern…" : "Speichern"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
